import { ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "../components/anim";
import Starfield from "../components/Starfield";
import { IMAGES } from "../data/site";

const VALUES = [
  { title: "Fixed prices, always", desc: "You'll never see an hourly invoice from us. The price we quote is the price you pay." },
  { title: "We answer fast", desc: "Most messages get a reply the same business day. Humans, not ticket bots." },
  { title: "Zero tech headaches", desc: "Hosting, security and updates are handled for you — you never touch a dashboard unless you want to." },
  { title: "Fast replies, always", desc: "Message us about any project — big or small — and you'll hear back within one business day." },
];

export default function About() {
  return (
    <main className="pt-20" data-testid="about-page">
      <section className="relative overflow-hidden py-20 lg:py-24">
        <div className="pointer-events-none absolute -top-32 right-[-8%] h-[420px] w-[420px] rounded-full bg-violet-600/15 blur-[130px]" />
        <Starfield />
        <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <Reveal>
            <Eyebrow>About G-Studio</Eyebrow>
            <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-white sm:text-6xl">
              A small studio with a simple obsession.
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-slate-400">
              G-Studio started with a frustration: small businesses paying thousands for websites that were abandoned
              the day after launch. Slow, outdated, insecure — and nobody's job to fix.
            </p>
            <p className="mt-4 text-lg leading-relaxed text-slate-400">
              So we made it simple: an expertly developed website launched in days, by a team that stays
              one call away after launch. That's the whole business model — and it works.
            </p>
            <div className="mt-8 flex gap-10">
              {[
                { v: "< 1 week", k: "Typical launch" },
                { v: "100%", k: "Custom design" },
                { v: "1 day", k: "Max. reply time" },
              ].map((s) => (
                <div key={s.k}>
                  <p className="font-display text-3xl font-bold text-white">{s.v}</p>
                  <p className="mt-1 font-mono2 text-[10px] uppercase tracking-[0.18em] text-slate-400">{s.k}</p>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="overflow-hidden rounded-3xl border border-white/10 shadow-2xl shadow-slate-900/10">
              <img src={IMAGES.team} alt="The G-Studio team at work" className="h-full w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>
      <section className="border-t border-white/10 bg-[#070B1E] py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Eyebrow>How We Work</Eyebrow>
            <h2 className="mt-4 max-w-xl font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Four promises we make to every client
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.07}>
                <div className="h-full rounded-2xl border border-white/10 bg-white/5 p-6" data-testid={`value-card-${i}`}>
                  <span className="font-display text-2xl font-bold text-blue-400/30">0{i + 1}</span>
                  <h3 className="mt-3 font-display text-lg font-semibold text-white">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12">
            <a
              href="tel:+15078411801"
              data-testid="about-call-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-blue-600 px-7 py-4 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
            >
              Call (507) 841-1801
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
